// bioapp/src/components/OrderChat.tsx
//
// Creator-side order chat for creator.shoutout.us (which serves the
// bioapp's BioDashboard at /dashboard). Mirrors the partnerprogram and
// main-app OrderChat components — same `order_messages` table, same
// `notify-order-message` edge function — but styled to match the dark
// glass UI of BioDashboard so the chat panel slots into an order card
// without looking like a foreign light-theme widget.
//
// When the talent posts a message, we insert into `order_messages` and
// fire the `notify-order-message` Edge Function which dispatches
// in-app + SMS + email notifications to the customer with a link back
// to https://shoutout.us/dashboard?order=<id> so they can reply or
// fill in missing request details.

import { useEffect, useRef, useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { supabase } from '../services/supabase';

const URL_REGEX = /\b((?:https?:\/\/|www\.)[^\s<]+|[a-z0-9-]+(?:\.[a-z0-9-]+)+\.[a-z]{2,}(?:\/[^\s<]*)?)/gi;
const containsUrl = (text: string) => {
  URL_REGEX.lastIndex = 0;
  return URL_REGEX.test(text);
};

interface OrderMessage {
  id: string;
  order_id: string;
  sender_id: string;
  sender_role: 'talent' | 'customer';
  message: string;
  is_read: boolean;
  created_at: string;
}

interface Props {
  orderId: string;
  currentUserId: string;
  customerName: string;
}

export default function OrderChat({ orderId, currentUserId, customerName }: Props) {
  const [messages, setMessages] = useState<OrderMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [unread, setUnread] = useState(0);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchMessages = async () => {
    const { data, error } = await supabase
      .from('order_messages')
      .select('*')
      .eq('order_id', orderId)
      .order('created_at', { ascending: true });
    if (!error && data) {
      setMessages(data as OrderMessage[]);
      setUnread((data as OrderMessage[]).filter((m) => m.sender_role !== 'talent' && !m.is_read).length);
    }
  };

  const markRead = async () => {
    const ids = messages.filter((m) => m.sender_role !== 'talent' && !m.is_read).map((m) => m.id);
    if (ids.length === 0) return;
    await supabase.from('order_messages').update({ is_read: true }).in('id', ids);
    setMessages((prev) => prev.map((m) => (ids.includes(m.id) ? { ...m, is_read: true } : m)));
    setUnread(0);
  };

  useEffect(() => {
    fetchMessages();
    const channel = supabase
      .channel(`order_messages_${orderId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'order_messages', filter: `order_id=eq.${orderId}` },
        (payload) => {
          const m = payload.new as OrderMessage;
          setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
          if (m.sender_role !== 'talent') {
            if (expanded) {
              supabase.from('order_messages').update({ is_read: true }).eq('id', m.id).then();
            } else {
              setUnread((p) => p + 1);
            }
          }
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (expanded) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
      markRead();
      setTimeout(() => inputRef.current?.focus(), 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, messages.length]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    if (containsUrl(body)) {
      toast.error('Links can only be shared by ShoutOut admins.');
      return;
    }
    setSending(true);
    const { error } = await supabase.from('order_messages').insert({
      order_id: orderId,
      sender_id: currentUserId,
      sender_role: 'talent',
      message: body,
    });
    if (error) {
      const msg = (error.message || '').toLowerCase();
      toast.error(
        msg.includes('link') || msg.includes('url')
          ? 'Links can only be shared by ShoutOut admins.'
          : 'Failed to send message',
      );
    } else {
      setText('');
      // Fire & forget — don't block the UI on email/SMS dispatch.
      supabase.functions
        .invoke('notify-order-message', { body: { order_id: orderId, message_preview: body } })
        .catch((err) => console.warn('notify-order-message error', err));
    }
    setSending(false);
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    const t = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (diff === 0) return t;
    if (diff === 1) return `Yesterday ${t}`;
    return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${t}`;
  };

  return (
    <div className="mt-3 rounded-xl bg-white/[0.04] border border-white/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3.5 py-2.5 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-gray-200">
          <ChatBubbleLeftRightIcon className="w-4 h-4 text-blue-400" />
          <span>
            Chat with customer{messages.length ? ` (${messages.length})` : ''}
          </span>
          {unread > 0 && (
            <span className="bg-red-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full animate-pulse">
              {unread} new
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUpIcon className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-white/10 bg-black/30">
          <div
            className="overflow-y-auto px-3 py-3 space-y-2.5"
            style={{ maxHeight: 280, minHeight: 80 }}
          >
            {messages.length === 0 ? (
              <p className="text-center text-xs text-gray-400 py-4">
                No messages yet. Send a quick note to {customerName} to get started.
              </p>
            ) : (
              messages.map((m) => {
                const mine = m.sender_role === 'talent';
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[82%] rounded-2xl px-3 py-1.5 ${
                        mine
                          ? 'bg-blue-600 text-white rounded-br-md'
                          : 'bg-white/10 text-gray-100 rounded-bl-md'
                      }`}
                    >
                      {!mine && (
                        <p className="text-[11px] font-medium mb-0.5 text-blue-300">
                          {customerName}
                        </p>
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                        {m.message}
                      </p>
                      <p
                        className={`text-[10px] mt-0.5 ${
                          mine ? 'text-blue-100' : 'text-gray-400'
                        }`}
                      >
                        {formatTime(m.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="flex items-center gap-2 px-2.5 py-2 border-t border-white/10">
            <input
              ref={inputRef}
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Message ${customerName}...`}
              maxLength={1000}
              className="flex-1 text-sm rounded-lg px-3 py-2 bg-white/[0.06] border border-white/15 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
            <button
              type="submit"
              disabled={!text.trim() || sending}
              className="p-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              <PaperAirplaneIcon className="w-4 h-4" />
            </button>
          </form>
          {containsUrl(text) && (
            <p className="px-3.5 pb-2 text-[11px] text-amber-400">
              Links can only be shared by ShoutOut admins. Please remove the URL before sending.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
